<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">OTA Campaigns</h1>
                <div class="d-flex align-center ga-3">
                    <v-btn-toggle v-model="showFilter" density="compact" variant="outlined" color="primary"
                                  @update:model-value="currentPage = 1; fetchCampaigns()">
                        <v-btn value="disabled" size="small">
                            <v-icon start :icon="showFilter === 'disabled' ? 'mdi-eye' : 'mdi-eye-off'"/>{{ showFilter === 'disabled' ? 'Hide Disabled' : 'Show Disabled' }}
                        </v-btn>
                        <v-btn value="trashed" size="small">
                            <v-icon start :icon="showFilter === 'trashed' ? 'mdi-delete-restore' : 'mdi-delete-outline'"/>{{ showFilter === 'trashed' ? 'Hide Deleted' : 'Show Deleted' }}
                        </v-btn>
                    </v-btn-toggle>

                    <v-btn icon="mdi-refresh" size="small" variant="tonal" @click="fetchCampaigns"/>

                    <v-btn color="black" prepend-icon="mdi-plus" @click="selectedCampaign = null; dialogSwitch = true">
                        Add
                    </v-btn>
                </div>
            </v-col>
        </v-row>
        <h3></h3>
        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th class="cursor-pointer" @click="toggleSort('id')">
                            ID <v-icon v-if="sortBy === 'id'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('name')">
                            Campaign Name <v-icon v-if="sortBy === 'name'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('package')">
                            OTA Package <v-icon v-if="sortBy === 'package'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('sku')">
                            SKU <v-icon v-if="sortBy === 'sku'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('device_group')">
                            Device Group <v-icon v-if="sortBy === 'device_group'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('status')">
                            Status <v-icon v-if="sortBy === 'status'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('max_retries')">
                            Max Retries <v-icon v-if="sortBy === 'max_retries'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('retry_interval')">
                            Retry Interval <v-icon v-if="sortBy === 'retry_interval'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('total_stats')">
                            Total <v-icon v-if="sortBy === 'total_stats'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th>Queued</th>
                        <th>In Progress</th>
                        <th>Success</th>
                        <th>Failed</th>
                        <th class="cursor-pointer" @click="toggleSort('progress')">
                            Progress <v-icon v-if="sortBy === 'progress'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('created_at')">
                            Created <v-icon v-if="sortBy === 'created_at'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('updated_at')">
                            Updated <v-icon v-if="sortBy === 'updated_at'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th>Notes</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="otaCampaign in otaCampaigns" v-if="!loading && otaCampaigns.length" :key="otaCampaign.id"
                        class="text-left" :class="{'bg-red-lighten-5': otaCampaign.deleted_at}">
                        <td>{{ otaCampaign.id }}</td>
                        <td>{{ otaCampaign.name }}</td>
                        <td>{{ otaCampaign.package }}</td>
                        <td>{{ otaCampaign.sku }}</td>
                        <td>{{ otaCampaign.device_group }}</td>
                        <td>
                            <v-icon :icon="otaCampaign.status?'mdi-check-circle':'mdi-close-circle'"/>
                        </td>
                        <td>{{ otaCampaign.max_retries }}</td>
                        <td>{{ otaCampaign.retry_interval }}</td>
                        <td>{{ otaCampaign.total_stats }}</td>
                        <td>{{ otaCampaign.started_stats }}</td>
                        <td>{{ otaCampaign.in_progress_stats }}</td>
                        <td>{{ otaCampaign.success_stats }}</td>
                        <td :class="{'text-red font-weight-bold': otaCampaign.failed_stats > 0}">
                            {{ otaCampaign.failed_stats }}
                        </td>
                        <td>
                            <div class="cursor-pointer" style="position: relative; height: 20px; border-radius: 4px; overflow: hidden; background: #e0e0e0"
                                 @click="showDevicesStatus(otaCampaign)">
                                <!-- Green = success percentage -->
                                <div v-if="otaCampaign.total_stats > 0"
                                     style="position: absolute; top: 0; left: 0; height: 100%; transition: width 0.3s"
                                     :style="{width: (otaCampaign.success_stats / otaCampaign.total_stats * 100) + '%', background: '#4CAF50'}"/>
                                <!-- Red = failed percentage (after green) -->
                                <div v-if="otaCampaign.total_stats > 0 && otaCampaign.failed_stats > 0"
                                     style="position: absolute; top: 0; height: 100%; transition: width 0.3s"
                                     :style="{left: (otaCampaign.success_stats / otaCampaign.total_stats * 100) + '%', width: (otaCampaign.failed_stats / otaCampaign.total_stats * 100) + '%', background: '#F44336'}"/>
                                <!-- Label -->
                                <small style="position: absolute; top: 0; left: 0; right: 0; height: 100%; display: flex; align-items: center; justify-content: center; z-index: 1"
                                       :class="{'text-white': otaCampaign.progress > 35}">
                                    {{ Math.ceil(otaCampaign.progress) }}%
                                </small>
                            </div>
                        </td>
                        <td>{{ formatDateTime(otaCampaign.created_at) }}</td>
                        <td>{{ formatDateTime(otaCampaign.updated_at) }}</td>
                        <td class="text-caption" style="max-width: 200px;">
                            <v-tooltip v-if="otaCampaign.notes" :text="otaCampaign.notes" location="top" max-width="300">
                                <template v-slot:activator="{ props }">
                                    <span v-bind="props" class="d-inline-block text-truncate" style="max-width: 200px;">{{ otaCampaign.notes }}</span>
                                </template>
                            </v-tooltip>
                        </td>
                        <td>
                            <v-btn-group v-if="!otaCampaign.deleted_at" density="compact" divided variant="outlined">
                                <v-btn :disabled="dialogSwitch" class="text-info"
                                       size="x-small" @click="campaignLogs(otaCampaign)">Logs
                                </v-btn>
                                <v-btn :disabled="dialogSwitch || otaCampaign.status === 1" class="text-warning"
                                       size="x-small" @click="editCampaign(otaCampaign)">Edit
                                    <v-tooltip v-if="otaCampaign.status === 1" activator="parent" location="top">Disable campaign before editing</v-tooltip>
                                </v-btn>
                                <v-btn :disabled="otaCampaign.status === 1" class="text-error" size="x-small"
                                       @click="deleteCampaign(otaCampaign)">Delete
                                    <v-tooltip v-if="otaCampaign.status === 1" activator="parent" location="top">Disable campaign before deleting</v-tooltip>
                                </v-btn>
                                <v-menu>
                                    <template v-slot:activator="{ props }">
                                        <v-btn icon="mdi-dots-vertical" v-bind="props" variant="text" size="x-small"/>
                                    </template>
                                    <v-list density="compact">
                                        <v-list-item
                                            :title="otaCampaign.status ? 'Disable Campaign' : 'Enable Campaign'"
                                            :prepend-icon="otaCampaign.status ? 'mdi-stop-circle-outline' : 'mdi-play-circle-outline'"
                                            :class="otaCampaign.status ? 'text-error' : 'text-success'"
                                            @click="toggleCampaignStatus(otaCampaign)"/>
                                        <v-list-item
                                            title="Requeue Failed"
                                            prepend-icon="mdi-refresh"
                                            :disabled="!otaCampaign.status || !otaCampaign.failed_stats"
                                            @click="requeueFailed(otaCampaign)"/>
                                        <v-list-item
                                            title="Force Requeue All"
                                            prepend-icon="mdi-restart"
                                            class="text-warning"
                                            :disabled="!otaCampaign.status"
                                            @click="forceRequeueAll(otaCampaign)"/>
                                    </v-list>
                                </v-menu>
                            </v-btn-group>
                            <v-btn-group v-else density="compact" divided variant="outlined">
                                <v-btn class="text-info" size="x-small" @click="campaignLogs(otaCampaign)">Logs</v-btn>
                                <v-menu>
                                    <template v-slot:activator="{ props }">
                                        <v-btn icon="mdi-dots-vertical" v-bind="props" variant="text" size="x-small"/>
                                    </template>
                                    <v-list density="compact">
                                        <v-list-item
                                            title="Permanently Delete"
                                            prepend-icon="mdi-delete-forever"
                                            class="text-error"
                                            @click="forceDeleteCampaign(otaCampaign)"/>
                                    </v-list>
                                </v-menu>
                            </v-btn-group>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="18">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="18">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>
        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-pagination
                    v-model="currentPage"
                    :length="totalPages"
                    class="my-4"
                    density="compact"
                    @update:model-value="fetchCampaigns"
                />
            </v-col>
        </v-row>
    </v-container>

    <CampaignLogs v-if="campaignLogsDialog" v-model="campaignLogsDialog" :campaign="selectedCampaign"/>
    <devicesStatus v-if="devicesStatusDialogSwitch" v-model="devicesStatusDialogSwitch" :campaign="selectedCampaign"/>
    <otaCampaign v-if="dialogSwitch" v-model="dialogSwitch" :otaCampaign="selectedCampaign"
                 @addCampaign="(newCampaign)=> otaCampaigns.unshift(newCampaign)"
                 @updateCampaign="(updatedCampaign) => {
                     const idx = otaCampaigns.findIndex(c => c.id === updatedCampaign.id);
                     if (idx !== -1) {
                         if (!showDisabled && !updatedCampaign.status) {
                             otaCampaigns.splice(idx, 1);
                         } else {
                             otaCampaigns[idx] = updatedCampaign;
                         }
                     }
                 }"/>
</template>

<script setup>
import {computed, onMounted, onBeforeUnmount, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from '@/composables/useTimezone';
import otaCampaign from "@/components/dialogs/ota/Campaign.vue";
import CampaignLogs from "@/components/dialogs/ota/CampaignLogs.vue";
import devicesStatus from "@/components/dialogs/ota/DevicesStatus.vue";

const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const otaCampaigns = ref([]);
const dialogSwitch = ref(false);
const {showSnackbar} = useSnackbarStore();
const selectedCampaign = ref(null);
const campaignLogsDialog = ref(false);
const devicesStatusDialogSwitch = ref(false);

const showFilter = ref(undefined);
const sortBy = ref('updated_at');
const sortDir = ref('desc');
const sortIcon = computed(() => sortDir.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down');

const toggleSort = (column) => {
    if (sortBy.value === column) {
        sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortBy.value = column;
        sortDir.value = 'asc';
    }
    currentPage.value = 1;
    fetchCampaigns();
};

let autoRefreshTimer = null;
onMounted(() => {
    fetchCampaigns();
    autoRefreshTimer = setInterval(() => {
        if (!loading.value) fetchCampaigns();
    }, 30000);
});
onBeforeUnmount(() => {
    if (autoRefreshTimer) clearInterval(autoRefreshTimer);
});

const fetchCampaigns = () => {
    loading.value = true
    const filterParam = showFilter.value === 'disabled' ? '&show_disabled=1' : showFilter.value === 'trashed' ? '&show_trashed=1' : '';
    const params = `page=${currentPage.value}&sort_by=${sortBy.value}&sort_dir=${sortDir.value}${filterParam}`;
    send('get', `admin/ota/campaigns?${params}`)
        .then((response) => {
            otaCampaigns.value = response.data.data.data;
            totalPages.value = response.data.data.last_page;
            currentPage.value = response.data.data.current_page;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('OTA Campaigns could not be loaded', 'error')
        })
        .finally(() => {
            loading.value = false
        })
}
const campaignLogs = (otaCampaign) => {
    selectedCampaign.value = otaCampaign;
    campaignLogsDialog.value = true;
}
const editCampaign = (otaCampaign) => {
    selectedCampaign.value = otaCampaign;
    dialogSwitch.value = true;
}
const showDevicesStatus = (otaCampaign) => {
    selectedCampaign.value = otaCampaign;
    devicesStatusDialogSwitch.value = true;
}
const deleteCampaign = (otaCampaign) => {
    if (confirm(`Are you sure to delete OTA Campaign for Device Group: "${otaCampaign.device_group}"?`)) {
        send('delete', `admin/ota/campaigns/${otaCampaign.id}`)
            .then((response) => {
                if (response.data.success) {
                    const idx = otaCampaigns.value.findIndex(c => c.id === otaCampaign.id);
                    if (idx !== -1) otaCampaigns.value.splice(idx, 1);
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response?.data?.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`OTA Campaign for Device Group: "${otaCampaign.device_group}" could not be deleted`, 'error')
                }
            })
    }
}

const toggleCampaignStatus = (otaCampaign) => {
    const newStatus = otaCampaign.status ? 0 : 1;
    const action = newStatus ? 'enable' : 'disable';
    if (!confirm(`Are you sure you want to ${action} campaign "${otaCampaign.name}"?`)) return;
    send('put', `admin/ota/campaigns/${otaCampaign.id}`, {status: newStatus})
        .then((response) => {
            if (response.data.success) {
                otaCampaign.status = newStatus;
                showSnackbar(response.data.message);
            }
        })
        .catch((error) => showSnackbar(error.response?.data?.message || `Failed to ${action} campaign`, 'error'));
}

const requeueFailed = (otaCampaign) => {
    if (!confirm('Requeue all failed devices in this campaign?')) return;
    send('post', `admin/ota/campaigns/${otaCampaign.id}/requeue-failed`)
        .then((response) => {
            if (response.data.success) {
                showSnackbar(response.data.message);
                fetchCampaigns();
            }
        })
        .catch((error) => showSnackbar(error.response?.data?.message || 'Failed to requeue', 'error'));
}

const forceDeleteCampaign = (otaCampaign) => {
    if (!confirm(`Permanently delete campaign "${otaCampaign.name}"? This will remove all device statuses and logs. This cannot be undone.`)) return;
    send('post', `admin/ota/campaigns/${otaCampaign.id}/force-delete`)
        .then((response) => {
            if (response.data.success) {
                const idx = otaCampaigns.value.findIndex(c => c.id === otaCampaign.id);
                if (idx !== -1) otaCampaigns.value.splice(idx, 1);
                showSnackbar(response.data.message);
            }
        })
        .catch((error) => showSnackbar(error.response?.data?.message || 'Failed to permanently delete campaign', 'error'));
}

const forceRequeueAll = (otaCampaign) => {
    if (!confirm('Force requeue ALL devices (including upgraded and downloading)? Active downloads will restart from 0%.')) return;
    send('post', `admin/ota/campaigns/${otaCampaign.id}/force-requeue-all`)
        .then((response) => {
            if (response.data.success) {
                showSnackbar(response.data.message);
                fetchCampaigns();
            }
        })
        .catch((error) => showSnackbar(error.response?.data?.message || 'Failed to force requeue', 'error'));
}
</script>
